/**
 * Project setup / import screen.
 * Route: /schedule/import
 * Owns the import flow for this route.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Progress,
  Modal,
  UnstyledButton,
  Divider,
} from '@mantine/core';
import LocationManagementPage from '../schedule-locations/LocationManagementPage';
import { useModalContainer } from '../../contexts/ModalContainerContext';
import {
  importWBSFromProjectFile,
  isImportPlanningConflictError,
  reassignLocationToTask,
  fetchAllTasks,
  normalizeTaskForReassign,
  type ImportWbsSetupMode,
  type LocationTreeNode,
  type ReassignTaskItem,
} from '../../api/schedulingApis';
import { getProjectId } from '../../utils/cookies';
import {
  getAllRequestsByCustomerID,
  invalidateRequestTrackingCache,
  isWbsSetupRequestActive,
  useActiveWbsSetupImport,
} from '../request-tracking';
import { useRequestTrackingModal } from '../../contexts/RequestTrackingModalContext';
import { getCustomerId } from '../../utils/cookies';
import { notifications } from '@mantine/notifications';
import { useLocationsTreeStore } from '../../stores/locationsTreeStore';
import { clearProjectData, loadTasksFromDB } from '../../stores/db';
import { getDashboardMemoryCache } from '../../dashboard/dashboardProjectCache';
import { IconCheck, IconUpload, IconCalendarEvent, IconMapPin } from '@tabler/icons-react';
import { APP_ROUTES } from '../../navigation/appRoutes';
import {
  PermissionRestrictedTooltip,
  useCanSchedulerAction,
} from '../../permissions/scheduler';
import { useFeatureTipsOptional } from '../feature-tips';
import { countMeaningfulLocations } from '../schedule-readiness';

const ALLOWED_EXTENSIONS = ['.mpp', '.msp', '.p6', '.xml'] as const;
const ALLOWED_FILE_ACCEPT = ALLOWED_EXTENSIONS.join(',');

const PROJECT_SETUP_STEPS = [
  { label: 'Import plan', description: 'Upload a Project Plan file' },
  { label: 'Manage locations & link tasks', description: 'Review locations and link tasks' },
] as const;

const EMPTY_LOCATION_TREE: LocationTreeNode[] = [];

/** Shown on import wizard locations step when hierarchy is empty / project-root only. */
function WeakLocationsImportCallout({ projectId }: { projectId: string }) {
  const tree = useLocationsTreeStore((s) =>
    projectId ? (s.treeByProjectId[projectId] ?? EMPTY_LOCATION_TREE) : EMPTY_LOCATION_TREE,
  );
  const hasLocationDraft = useLocationsTreeStore((s) =>
    projectId ? s.locationDraftByProjectId[projectId] != null : false,
  );
  const meaningful = React.useMemo(() => {
    if (hasLocationDraft) {
      const effective =
        useLocationsTreeStore.getState().getLocationDraftEffectiveTree(projectId) ?? [];
      return countMeaningfulLocations(effective);
    }
    return countMeaningfulLocations(tree);
  }, [hasLocationDraft, tree, projectId]);

  if (meaningful > 0) return null;

  return (
    <Alert color="yellow" variant="light" title="Couldn’t derive a useful site structure" mb="md">
      <Text size="sm">
        Build or edit locations here, then Finish. Linking may leave tasks unassigned if locations
        stay sparse — Schedule readiness on Tasks will keep guiding you.
      </Text>
    </Alert>
  );
}

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  if (idx < 0) return '';
  return fileName.slice(idx).toLowerCase();
}

function isAllowedPlanFile(file: File): boolean {
  return (ALLOWED_EXTENSIONS as readonly string[]).includes(getFileExtension(file.name));
}

const ImportPlanningDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { openRequestTracker } = useRequestTrackingModal();
  const featureTips = useFeatureTipsOptional();
  const canImportRun = useCanSchedulerAction('importRun');
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lastImportRefId, setLastImportRefId] = useState<string | null>(null);
  const [runLinking, setRunLinking] = useState(false);
  const [finishConfirmOpened, setFinishConfirmOpened] = useState(false);
  const [replaceConfirmOpened, setReplaceConfirmOpened] = useState(false);
  const [pendingReplaceFile, setPendingReplaceFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalContainer = useModalContainer();
  const projectId = getProjectId() || '';
  const { importInProgress, activeRequest, loading: importGuardLoading, refresh: refreshImportGuard } =
    useActiveWbsSetupImport(10000);

  const step1ActionsBlocked = importGuardLoading || importInProgress;
  const canProceedToStep2 = Boolean(lastImportRefId) && !step1ActionsBlocked;
  const dropzoneDisabled = !canImportRun || uploading || importInProgress;
  /** Progress UI only while a real upload/job is running — not while the guard is first loading. */
  const showImportProgress = uploading || importInProgress;
  /** Blank schedule and locations are not relevant once the user has chosen a file upload. */
  const otherStartOptionsDisabled = showImportProgress;
  /** When the project already has tasks, only import/replace remains on step 1. */
  const [hasExistingTasks, setHasExistingTasks] = useState(false);

  useEffect(() => {
    featureTips?.maybeShowTip('import-planning');
  }, [featureTips?.maybeShowTip]);

  useEffect(() => {
    if (!projectId) {
      setHasExistingTasks(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const cached = getDashboardMemoryCache(projectId);
        if (cached?.tasks?.length) {
          if (!cancelled) setHasExistingTasks(true);
          return;
        }
        const fromDb = await loadTasksFromDB(projectId);
        if (fromDb?.tasks?.length) {
          if (!cancelled) setHasExistingTasks(true);
          return;
        }
        const res = await fetchAllTasks({ projectId });
        const raw = Array.isArray((res as { data?: unknown })?.data)
          ? (res as { data: unknown[] }).data
          : Array.isArray(res)
            ? res
            : [];
        if (!cancelled) setHasExistingTasks(raw.length > 0);
      } catch {
        if (!cancelled) setHasExistingTasks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, lastImportRefId, importInProgress]);

  const notifyUploadRequired = useCallback(() => {
    notifications.show({
      title: 'Upload required',
      message:
        'Upload a Project Plan file and wait for the import to finish before continuing to location management.',
      color: 'yellow',
    });
  }, []);

  useEffect(() => {
    if (activeStep === 1 && !canProceedToStep2) {
      setActiveStep(0);
    }
  }, [activeStep, canProceedToStep2]);

  const pendingCompletionRequestIdRef = useRef<string | null>(null);
  const autoAdvancedForRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (importGuardLoading || activeStep !== 0) {
      return;
    }

    if (importInProgress && activeRequest?.requestId) {
      pendingCompletionRequestIdRef.current = activeRequest.requestId;
      return;
    }

    const requestId = pendingCompletionRequestIdRef.current;
    if (!requestId || importInProgress) {
      return;
    }
    if (autoAdvancedForRequestIdRef.current === requestId) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const items = await getAllRequestsByCustomerID(getCustomerId());
        if (cancelled) {
          return;
        }
        const item = items.find((entry) => entry.requestId === requestId);
        if (!item || isWbsSetupRequestActive(item)) {
          return;
        }

        pendingCompletionRequestIdRef.current = null;

        if (item.status === 'completed') {
          autoAdvancedForRequestIdRef.current = requestId;
          setActiveStep(1);
          notifications.show({
            title: 'Import complete',
            message: item.fileName
              ? `“${item.fileName}” finished processing. Continuing to location management.`
              : 'Your Project Plan import finished. Continuing to location management.',
            color: 'green',
          });
        }
      } catch {
        // Polling will retry on the next guard refresh.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    activeRequest?.requestId,
    importGuardLoading,
    importInProgress,
  ]);

  const goNext = useCallback(() => {
    if (step1ActionsBlocked) return;
    if (!canProceedToStep2) {
      notifyUploadRequired();
      return;
    }
    setActiveStep((s) => Math.min(s + 1, 1));
  }, [canProceedToStep2, notifyUploadRequired, step1ActionsBlocked]);

  const handleStepClick = useCallback(
    (step: number) => {
      if (step > 0) {
        if (step1ActionsBlocked) return;
        if (!canProceedToStep2) {
          notifyUploadRequired();
          return;
        }
      }
      setActiveStep(step);
    },
    [canProceedToStep2, notifyUploadRequired, step1ActionsBlocked],
  );
  const goPrev = () => setActiveStep((s) => Math.max(s - 1, 0));

  const runImport = useCallback(
    async (file: File, mode: ImportWbsSetupMode) => {
      setUploading(true);
      try {
        const res = await importWBSFromProjectFile({ projectId, file, mode });
        const trackingId = res.requestId || res.refId;
        setLastImportRefId(trackingId);
        setPendingReplaceFile(null);
        setReplaceConfirmOpened(false);
        invalidateRequestTrackingCache();
        void refreshImportGuard();
        notifications.show({
          title: 'Import started',
          message: trackingId
            ? `Request ID: ${trackingId}. Monitor progress in the request tracker.`
            : 'Your Project Plan file is being processed in the background.',
          color: 'teal',
        });
        if (trackingId) {
          openRequestTracker({ refreshSignal: trackingId });
        }
        return true;
      } catch (err) {
        if (mode === 'merge' && isImportPlanningConflictError(err)) {
          setPendingReplaceFile(file);
          setReplaceConfirmOpened(true);
          return false;
        }
        // Non-conflict errors already show a notification from the API helper.
        return false;
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [openRequestTracker, projectId, refreshImportGuard],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      if (!canImportRun) {
        notifications.show({
          title: 'Permission denied',
          message: 'You do not have permission to import planning data.',
          color: 'red',
        });
        return;
      }
      if (step1ActionsBlocked || uploading) {
        return;
      }
      if (!projectId) {
        notifications.show({
          title: 'No project selected',
          message: 'Please select a project before importing a Project Plan file.',
          color: 'red',
        });
        return;
      }
      if (!isAllowedPlanFile(file)) {
        notifications.show({
          title: 'Unsupported file type',
          message: `Choose a ${ALLOWED_EXTENSIONS.join(', ')} file.`,
          color: 'red',
        });
        return;
      }

      await runImport(file, 'merge');
    },
    [canImportRun, projectId, runImport, step1ActionsBlocked, uploading],
  );

  const handleReplaceConfirm = useCallback(async () => {
    if (!pendingReplaceFile || uploading) return;
    await runImport(pendingReplaceFile, 'replace');
  }, [pendingReplaceFile, runImport, uploading]);

  const handleReplaceCancel = useCallback(() => {
    if (uploading) return;
    setReplaceConfirmOpened(false);
    setPendingReplaceFile(null);
  }, [uploading]);

  const acceptFile = useCallback(
    (file: File | null | undefined) => {
      if (!file || dropzoneDisabled) return;
      void uploadFile(file);
    },
    [dropzoneDisabled, uploadFile],
  );

  const handleDropzoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (dropzoneDisabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (dropzoneDisabled) return;
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (dropzoneDisabled) return;
    const file = event.dataTransfer.files?.[0];
    acceptFile(file);
  };

  const handleRunLinking = async (): Promise<boolean> => {
    if (!projectId) {
      notifications.show({ title: 'Error', message: 'No project selected.', color: 'red' });
      return false;
    }
    setRunLinking(true);
    try {
      const store = useLocationsTreeStore.getState();

      const tasksRes = await fetchAllTasks({ projectId });
      const tasksRaw = (tasksRes as any)?.data ?? tasksRes;
      const rawTasksArr = Array.isArray(tasksRaw) ? tasksRaw : [];
      const parentMap = new Map<string, string | null>();
      for (const t of rawTasksArr) {
        const id = t?.id ?? t?.task_id ?? t?.taskId;
        const pid = t?.parentTaskId ?? t?.parent_task_id ?? null;
        if (id != null) parentMap.set(String(id), pid != null ? String(pid) : null);
      }

      // Always the saved tree. Classification used to be reviewable in a separate in-memory
      // mode that could be linked from here without ever saving; it now lands in the location
      // draft, so the hierarchy has to be saved before it can be linked.
      const tree = store.getTree(projectId) ?? (await store.fetchTreeIfNeeded(projectId));
      const location_hierarchy: LocationTreeNode[] = tree ?? [];
      const tasks: ReassignTaskItem[] = rawTasksArr.map(normalizeTaskForReassign);

      await reassignLocationToTask({ projectId, location_hierarchy, tasks });
      await clearProjectData(projectId);
      await store.clearTree(projectId);
      notifications.show({
        title: 'Linking complete',
        message: 'Task–location mapping has been updated. Redirecting to scheduler...',
        color: 'teal',
      });
      return true;
    } catch {
      // Error notification already shown by API helper.
      return false;
    } finally {
      setRunLinking(false);
    }
  };

  const handleFinishConfirm = async () => {
    const ok = await handleRunLinking();
    if (ok) {
      setFinishConfirmOpened(false);
      navigate(APP_ROUTES.schedule.tasks);
    }
  };

  const renderStepHeaderNav = () => (
    <Group gap="xs" wrap="nowrap">
      {activeStep > 0 ? (
        <Button variant="subtle" size="xs" onClick={goPrev} disabled={runLinking}>
          Previous
        </Button>
      ) : null}
      {activeStep === 1 || canProceedToStep2 ? (
        <Button
          className="project-setup-nav-btn"
          size="xs"
          onClick={activeStep === 1 ? () => setFinishConfirmOpened(true) : goNext}
          disabled={activeStep === 1 && runLinking}
          loading={activeStep === 1 && runLinking}
        >
          {activeStep === 1 ? 'Finish' : 'Next'}
        </Button>
      ) : null}
    </Group>
  );

  const progressPercent =
    activeRequest?.progress != null
      ? Math.max(0, Math.min(100, activeRequest.progress))
      : null;

  const renderTopbarStepper = () => (
    <nav className="project-setup-steps" aria-label="Project setup steps">
      <ol className="project-setup-steps__list">
        {PROJECT_SETUP_STEPS.map((step, index) => {
          const isActive = activeStep === index;
          const isDone = activeStep > index;
          const isLast = index === PROJECT_SETUP_STEPS.length - 1;
          const stateClass = isActive
            ? 'project-setup-step--active'
            : isDone
              ? 'project-setup-step--done'
              : 'project-setup-step--upcoming';

          return (
            <li key={step.label} className={`project-setup-step ${stateClass}`}>
              <UnstyledButton
                className="project-setup-step__button"
                onClick={() => handleStepClick(index)}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="project-setup-step__marker" aria-hidden>
                  {isDone ? <IconCheck size={12} stroke={2.5} /> : index + 1}
                </span>
                <span className="project-setup-step__label">{step.label}</span>
              </UnstyledButton>
              {!isLast ? <span className="project-setup-step__connector" aria-hidden /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  return (
    <Box className="project-setup-page">
      <Box className="project-setup-topbar">
        <Text className="project-setup-topbar__title">Get started</Text>
        <Box className="project-setup-topbar__trail">
          {activeStep === 1 || canProceedToStep2 ? renderTopbarStepper() : null}
          <Box className="project-setup-topbar__actions">{renderStepHeaderNav()}</Box>
        </Box>
      </Box>

      <Box className="project-setup-body">
        <Box className="project-setup-main">
          {activeStep === 0 && (
            <Box className="project-setup-step1-column">
              <Box className="project-setup-main-header">
                <Text className="project-setup-eyebrow">Project setup</Text>
                <Text className="project-setup-title">
                  {hasExistingTasks ? 'Import or replace a plan' : 'Choose how to begin'}
                </Text>
                <Text size="sm" c="dimmed" mt={6}>
                  {hasExistingTasks
                    ? 'This project already has schedule tasks. Upload a plan file to merge or replace planning data.'
                    : 'Import a plan, start blank on the Gantt, or build locations first — you can always finish linking tasks and sites later.'}
                </Text>
              </Box>

              <Box className="project-setup-step1">
                <Box className="project-setup-card">
                  <Box className="project-setup-option project-setup-option--stack">
                    <Box className="project-setup-option__content">
                      <Box className="project-setup-option__heading">
                        <Box className="project-setup-option__icon" aria-hidden>
                          <IconUpload size={18} stroke={1.75} />
                        </Box>
                        <Text className="project-setup-option__title">Import an existing plan</Text>
                      </Box>
                      <Text className="project-setup-option__desc">
                        Upload MPP, MSP, P6, or XML. We propose a location structure from the plan —
                        it is a starting point and may be weak if the file is not site-structured.
                        You can edit locations after upload.
                      </Text>
                    </Box>

                    <PermissionRestrictedTooltip restricted={!canImportRun} fullWidth>
                      <Box
                        className={[
                          'project-setup-dropzone',
                          dragActive ? 'project-setup-dropzone--active' : '',
                          dropzoneDisabled ? 'project-setup-dropzone--disabled' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        role="button"
                        tabIndex={dropzoneDisabled ? -1 : 0}
                        aria-disabled={dropzoneDisabled}
                        aria-label="Drop a plan file here or click to browse"
                        onClick={() => {
                          if (!dropzoneDisabled) fileInputRef.current?.click();
                        }}
                        onKeyDown={handleDropzoneKeyDown}
                        onDragEnter={handleDragOver}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ALLOWED_FILE_ACCEPT}
                          style={{ display: 'none' }}
                          disabled={dropzoneDisabled}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            acceptFile(file);
                          }}
                        />
                        <Text className="project-setup-dropzone__title">
                          {uploading ? 'Uploading…' : 'Drop a plan file here'}
                        </Text>
                        <Text className="project-setup-dropzone__hint">
                          or click to browse {ALLOWED_EXTENSIONS.join(' ')}
                        </Text>
                      </Box>
                    </PermissionRestrictedTooltip>

                    {showImportProgress && (
                      <Alert
                        color="yellow"
                        variant="light"
                        title={uploading && !importInProgress ? 'Uploading file' : 'Import in progress'}
                        mt="md"
                      >
                        <Text size="sm">
                          {uploading && !importInProgress
                            ? 'Your Project Plan file is being uploaded. Progress will appear once processing starts.'
                            : `You have a running Project Plan import${
                                activeRequest?.fileName ? ` (“${activeRequest.fileName}”)` : ''
                              }. Wait for it to finish before uploading another file or continuing to the next step.`}
                        </Text>
                        <Box mt="sm">
                          <Group justify="space-between" mb={4}>
                            <Text size="xs" c="dimmed">
                              Progress
                            </Text>
                            <Text size="xs" fw={600}>
                              {progressPercent != null
                                ? `${Math.round(progressPercent)}%`
                                : uploading && !importInProgress
                                  ? 'Uploading…'
                                  : '—'}
                            </Text>
                          </Group>
                          <Progress
                            value={progressPercent ?? (uploading && !importInProgress ? 15 : 0)}
                            size="sm"
                            animated
                            striped={progressPercent == null}
                          />
                        </Box>
                      </Alert>
                    )}

                    {lastImportRefId && !showImportProgress && (
                      <Text size="xs" c="dimmed" mt="sm">
                        Last import request ID: {lastImportRefId}
                      </Text>
                    )}
                  </Box>

                  {!hasExistingTasks ? (
                    <>
                      <Divider className="project-setup-or" label="or" labelPosition="center" my="lg" />

                      <Box
                        className={[
                          'project-setup-option project-setup-option--with-action',
                          otherStartOptionsDisabled ? 'project-setup-option--unavailable' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-disabled={otherStartOptionsDisabled}
                      >
                        <Box className="project-setup-option__icon" aria-hidden>
                          <IconCalendarEvent size={18} stroke={1.75} />
                        </Box>
                        <Box className="project-setup-option__content">
                          <Text className="project-setup-option__title">
                            Start with a blank schedule
                          </Text>
                          <Text className="project-setup-option__desc">
                            Open the Gantt and add a root task. Build locations and assign them when
                            you are ready.
                          </Text>
                        </Box>
                        <span
                          title={
                            otherStartOptionsDisabled
                              ? 'Unavailable while a plan file is uploading or importing.'
                              : undefined
                          }
                        >
                          <Button
                            className="project-setup-scratch-btn"
                            disabled={otherStartOptionsDisabled}
                            onClick={() =>
                              navigate(`${APP_ROUTES.schedule.tasks}?started=blank`)
                            }
                          >
                            Open Tasks
                          </Button>
                        </span>
                      </Box>

                      <Divider className="project-setup-or" label="or" labelPosition="center" my="lg" />

                      <Box
                        className={[
                          'project-setup-option project-setup-option--with-action',
                          otherStartOptionsDisabled ? 'project-setup-option--unavailable' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-disabled={otherStartOptionsDisabled}
                      >
                        <Box className="project-setup-option__icon" aria-hidden>
                          <IconMapPin size={18} stroke={1.75} />
                        </Box>
                        <Box className="project-setup-option__content">
                          <Text className="project-setup-option__title">Build locations first</Text>
                          <Text className="project-setup-option__desc">
                            Set up your site hierarchy, then create or import tasks and assign
                            locations on the Gantt.
                          </Text>
                        </Box>
                        <span
                          title={
                            otherStartOptionsDisabled
                              ? 'Unavailable while a plan file is uploading or importing.'
                              : undefined
                          }
                        >
                          <Button
                            className="project-setup-scratch-btn"
                            disabled={otherStartOptionsDisabled}
                            onClick={() => navigate(APP_ROUTES.schedule.locations)}
                          >
                            Open Location management
                          </Button>
                        </span>
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box className="project-setup-step2">
              <WeakLocationsImportCallout projectId={projectId} />
              <LocationManagementPage titleOverride="Manage locations & link tasks" />
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        opened={replaceConfirmOpened}
        onClose={handleReplaceCancel}
        title="Replace planning data?"
        centered
        closeOnClickOutside={!uploading}
        closeOnEscape={!uploading}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
      >
        <Stack gap="md">
          <Text size="sm">
            Project already has planning data. Do you want to replace the current data?
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleReplaceCancel} disabled={uploading}>
              No
            </Button>
            <Button loading={uploading} onClick={() => void handleReplaceConfirm()}>
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={finishConfirmOpened}
        onClose={() => {
          if (!runLinking) {
            setFinishConfirmOpened(false);
          }
        }}
        title="Finish location setup?"
        centered
        closeOnClickOutside={!runLinking}
        closeOnEscape={!runLinking}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
      >
        <Stack gap="md">
          <Text size="sm">
            We&apos;ll link your locations to the right tasks and save those updates to the project
            schedule.
          </Text>
          <Text size="sm" c="dimmed">
            If the location tree is still thin, some tasks may stay unassigned. You can finish
            assignment later on the Gantt — Schedule readiness will remind you.
          </Text>
          <Text size="sm" c="dimmed">
            When this is done, you&apos;ll be taken to the Gantt chart to review your plan.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setFinishConfirmOpened(false)}
              disabled={runLinking}
            >
              Cancel
            </Button>
            <Button loading={runLinking} onClick={() => void handleFinishConfirm()}>
              Finish
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default ImportPlanningDashboard;
